import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, GripVertical, ChevronDown, ChevronUp, Eye, EyeOff, Save, Loader2, FileText, ArrowUp, ArrowDown, Upload, ImageIcon, Link2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function AdminPagesList() {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/api/admin/pages`, { withCredentials: true });
                setPages(res.data);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <DashboardLayout type="admin"><div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div></DashboardLayout>;

    return (
        <DashboardLayout type="admin">
            <div className="space-y-6" data-testid="admin-pages-list">
                <div>
                    <h1 className="text-2xl font-bold text-white">Page Manager</h1>
                    <p className="text-sm text-muted-foreground mt-1">Edit content, reorder sections, and toggle visibility</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {pages.map(page => (
                        <Card key={page.slug} className="bg-[#121620] border-white/10 hover:border-gold/20 transition-colors cursor-pointer" onClick={() => navigate(`/admin/pages/${page.slug}`)} data-testid={`page-card-${page.slug}`}>
                            <CardContent className="p-5">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                                        <FileText className="w-5 h-5 text-gold" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white">{page.title}</h3>
                                        <p className="text-xs text-white/30">{page.section_count} section{page.section_count !== 1 ? 's' : ''}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </DashboardLayout>
    );
}

export default function AdminPageEditor() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const fetchPage = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/admin/pages/${slug}`, { withCredentials: true });
            setPage(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [slug]);

    useEffect(() => { fetchPage(); }, [fetchPage]);

    const toggleSection = async (sectionId, enabled) => {
        try {
            await axios.put(`${API}/api/admin/pages/${slug}/sections/${sectionId}`, { enabled }, { withCredentials: true });
            setPage(prev => ({
                ...prev,
                sections: prev.sections.map(s => s.id === sectionId ? { ...s, enabled } : s)
            }));
            toast.success(enabled ? 'Section enabled' : 'Section hidden');
        } catch (e) { toast.error('Failed to update'); }
    };

    const moveSection = async (index, direction) => {
        const sections = [...page.sections];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= sections.length) return;
        [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
        sections.forEach((s, i) => { s.order = i; });
        setPage(prev => ({ ...prev, sections }));
        try {
            await axios.post(`${API}/api/admin/pages/${slug}/reorder`, { section_ids: sections.map(s => s.id) }, { withCredentials: true });
        } catch (e) { toast.error('Failed to reorder'); }
    };

    const updateSectionContent = (sectionId, field, value) => {
        setPage(prev => ({
            ...prev,
            sections: prev.sections.map(s => {
                if (s.id !== sectionId) return s;
                if (field.includes('.')) {
                    const [parent, child, idx, key] = field.split('.');
                    const content = { ...s.content };
                    if (idx !== undefined && key !== undefined) {
                        const arr = [...(content[parent] || [])];
                        arr[parseInt(child)] = { ...arr[parseInt(child)], [idx]: value };
                        content[parent] = arr;
                    } else {
                        content[parent] = value;
                    }
                    return { ...s, content };
                }
                return { ...s, content: { ...s.content, [field]: value } };
            })
        }));
    };

    const saveSection = async (sectionId) => {
        setSaving(true);
        const section = page.sections.find(s => s.id === sectionId);
        try {
            await axios.put(`${API}/api/admin/pages/${slug}/sections/${sectionId}`, { content: section.content }, { withCredentials: true });
            toast.success('Section saved');
        } catch (e) { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const renderContentEditor = (section) => {
        const c = section.content || {};
        const update = (field, val) => updateSectionContent(section.id, field, val);

        switch (section.type) {
            case 'hero':
                const handleHeroImageUpload = async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append('file', file);
                    try {
                        const res = await axios.post(`${API}/api/files/upload`, fd, { withCredentials: true });
                        update('bg_image', `/api/files/${res.data.path}`);
                        toast.success('Image uploaded');
                    } catch { toast.error('Upload failed'); }
                };
                return (
                    <div className="space-y-4">
                        <div><Label className="text-white/50 text-xs">Title</Label><Input value={c.title || ''} onChange={e => update('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <div><Label className="text-white/50 text-xs">Subtitle</Label><Input value={c.subtitle || ''} onChange={e => update('subtitle', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <div>
                            <Label className="text-white/50 text-xs mb-2 block">Background Image</Label>
                            {c.bg_image && (
                                <div className="mb-3 relative rounded-lg overflow-hidden border border-white/10" style={{ maxHeight: 160 }}>
                                    <img
                                        src={c.bg_image.startsWith('/api') ? `${API}${c.bg_image}` : c.bg_image}
                                        alt="Hero background"
                                        className="w-full h-40 object-cover"
                                    />
                                    <button onClick={() => update('bg_image', '')} className="absolute top-2 right-2 bg-black/70 text-white/70 hover:text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">&times;</button>
                                </div>
                            )}
                            <div className="flex gap-2">
                                <label className="flex-1 cursor-pointer">
                                    <input type="file" accept="image/*" onChange={handleHeroImageUpload} className="hidden" data-testid="hero-image-upload" />
                                    <div className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-white/15 bg-white/[0.03] hover:border-gold/30 hover:bg-white/[0.05] transition-colors text-sm text-white/50 hover:text-white">
                                        <Upload className="w-4 h-4" /> Upload Image
                                    </div>
                                </label>
                                <div className="text-white/20 flex items-center text-xs">or</div>
                                <div className="flex-1 relative">
                                    <Link2 className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                                    <Input
                                        value={c.bg_image?.startsWith('/api') ? '' : (c.bg_image || '')}
                                        onChange={e => update('bg_image', e.target.value)}
                                        placeholder="Paste image URL..."
                                        className="bg-white/5 border-white/10 text-white pl-9 text-sm"
                                        data-testid="hero-image-url"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'header':
                return (
                    <div className="space-y-3">
                        <div><Label className="text-white/50 text-xs">Title</Label><Input value={c.title || ''} onChange={e => update('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <div><Label className="text-white/50 text-xs">Subtitle</Label><Textarea value={c.subtitle || ''} onChange={e => update('subtitle', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 min-h-[60px]" /></div>
                    </div>
                );
            case 'cities':
            case 'listings':
            case 'features':
                return (
                    <div className="space-y-3">
                        <div><Label className="text-white/50 text-xs">Section Label</Label><Input value={c.section_label || ''} onChange={e => update('section_label', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <div><Label className="text-white/50 text-xs">Title</Label><Input value={c.title || ''} onChange={e => update('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        {c.subtitle !== undefined && <div><Label className="text-white/50 text-xs">Subtitle</Label><Input value={c.subtitle || ''} onChange={e => update('subtitle', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>}
                    </div>
                );
            case 'how_it_works':
                return (
                    <div className="space-y-4">
                        <div><Label className="text-white/50 text-xs">Section Label</Label><Input value={c.section_label || ''} onChange={e => update('section_label', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <div><Label className="text-white/50 text-xs">Title</Label><Input value={c.title || ''} onChange={e => update('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <Label className="text-white/50 text-xs">Steps</Label>
                        {(c.steps || []).map((step, i) => (
                            <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-2">
                                <div className="flex items-center gap-2"><span className="text-gold text-xs font-bold">Step {i + 1}</span></div>
                                <Input value={step.title || ''} onChange={e => { const steps = [...c.steps]; steps[i] = { ...steps[i], title: e.target.value }; update('steps', steps); }} placeholder="Step title" className="bg-white/5 border-white/10 text-white text-sm" />
                                <Textarea value={step.desc || ''} onChange={e => { const steps = [...c.steps]; steps[i] = { ...steps[i], desc: e.target.value }; update('steps', steps); }} placeholder="Step description" className="bg-white/5 border-white/10 text-white text-sm min-h-[50px]" />
                            </div>
                        ))}
                    </div>
                );
            case 'stats':
                return (
                    <div className="space-y-3">
                        <Label className="text-white/50 text-xs">Stats Items</Label>
                        {(c.items || []).map((item, i) => (
                            <div key={i} className="flex gap-2">
                                <Input value={item.value || ''} onChange={e => { const items = [...c.items]; items[i] = { ...items[i], value: e.target.value }; update('items', items); }} placeholder="Value" className="bg-white/5 border-white/10 text-white text-sm w-24" />
                                <Input value={item.label || ''} onChange={e => { const items = [...c.items]; items[i] = { ...items[i], label: e.target.value }; update('items', items); }} placeholder="Label" className="bg-white/5 border-white/10 text-white text-sm flex-1" />
                                <Input value={item.suffix || ''} onChange={e => { const items = [...c.items]; items[i] = { ...items[i], suffix: e.target.value }; update('items', items); }} placeholder="Suffix" className="bg-white/5 border-white/10 text-white text-sm w-16" />
                            </div>
                        ))}
                    </div>
                );
            case 'why_choose':
                return (
                    <div className="space-y-4">
                        <div><Label className="text-white/50 text-xs">Section Label</Label><Input value={c.section_label || ''} onChange={e => update('section_label', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <div><Label className="text-white/50 text-xs">Title</Label><Input value={c.title || ''} onChange={e => update('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <Label className="text-white/50 text-xs">Advantage Cards</Label>
                        {(c.items || []).map((item, i) => (
                            <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-2">
                                <Input value={item.title || ''} onChange={e => { const items = [...c.items]; items[i] = { ...items[i], title: e.target.value }; update('items', items); }} placeholder="Title" className="bg-white/5 border-white/10 text-white text-sm" />
                                <Textarea value={item.desc || ''} onChange={e => { const items = [...c.items]; items[i] = { ...items[i], desc: e.target.value }; update('items', items); }} placeholder="Description" className="bg-white/5 border-white/10 text-white text-sm min-h-[50px]" />
                            </div>
                        ))}
                    </div>
                );
            case 'testimonials':
                return (
                    <div className="space-y-4">
                        <div><Label className="text-white/50 text-xs">Section Label</Label><Input value={c.section_label || ''} onChange={e => update('section_label', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <div><Label className="text-white/50 text-xs">Title</Label><Input value={c.title || ''} onChange={e => update('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <Label className="text-white/50 text-xs">Reviews</Label>
                        {(c.reviews || []).map((rev, i) => (
                            <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-2">
                                <div className="flex gap-2">
                                    <Input value={rev.name || ''} onChange={e => { const reviews = [...c.reviews]; reviews[i] = { ...reviews[i], name: e.target.value }; update('reviews', reviews); }} placeholder="Name" className="bg-white/5 border-white/10 text-white text-sm" />
                                    <Input value={rev.location || ''} onChange={e => { const reviews = [...c.reviews]; reviews[i] = { ...reviews[i], location: e.target.value }; update('reviews', reviews); }} placeholder="Location" className="bg-white/5 border-white/10 text-white text-sm" />
                                </div>
                                <Textarea value={rev.text || ''} onChange={e => { const reviews = [...c.reviews]; reviews[i] = { ...reviews[i], text: e.target.value }; update('reviews', reviews); }} placeholder="Review text" className="bg-white/5 border-white/10 text-white text-sm min-h-[60px]" />
                            </div>
                        ))}
                    </div>
                );
            case 'partner_cta':
                return (
                    <div className="space-y-3">
                        <div><Label className="text-white/50 text-xs">Title</Label><Input value={c.title || ''} onChange={e => update('title', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <div><Label className="text-white/50 text-xs">Subtitle</Label><Textarea value={c.subtitle || ''} onChange={e => update('subtitle', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 min-h-[60px]" /></div>
                        <div className="flex gap-2">
                            <div className="flex-1"><Label className="text-white/50 text-xs">Button Text</Label><Input value={c.button_text || ''} onChange={e => update('button_text', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                            <div className="flex-1"><Label className="text-white/50 text-xs">Button Link</Label><Input value={c.button_link || ''} onChange={e => update('button_link', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        </div>
                    </div>
                );
            case 'info':
                return (
                    <div className="space-y-3">
                        <div><Label className="text-white/50 text-xs">Email</Label><Input value={c.email || ''} onChange={e => update('email', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                        <div><Label className="text-white/50 text-xs">Response Time Note</Label><Input value={c.response_time || ''} onChange={e => update('response_time', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                    </div>
                );
            case 'richtext':
                return (
                    <div>
                        <Label className="text-white/50 text-xs">Content (HTML supported)</Label>
                        <Textarea value={c.text || ''} onChange={e => update('text', e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 min-h-[300px] font-mono text-sm" placeholder="Enter page content here... HTML tags are supported." />
                    </div>
                );
            case 'form':
                return <p className="text-white/30 text-sm">The contact form is auto-rendered. No editable content.</p>;
            default:
                return (
                    <div className="space-y-3">
                        {Object.entries(c).map(([key, val]) => (
                            <div key={key}>
                                <Label className="text-white/50 text-xs capitalize">{key.replace(/_/g, ' ')}</Label>
                                {typeof val === 'string' ? (
                                    val.length > 100
                                        ? <Textarea value={val} onChange={e => update(key, e.target.value)} className="bg-white/5 border-white/10 text-white mt-1 min-h-[80px]" />
                                        : <Input value={val} onChange={e => update(key, e.target.value)} className="bg-white/5 border-white/10 text-white mt-1" />
                                ) : null}
                            </div>
                        ))}
                    </div>
                );
        }
    };

    if (loading) return <DashboardLayout type="admin"><div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div></DashboardLayout>;
    if (!page) return <DashboardLayout type="admin"><p className="text-white/40">Page not found</p></DashboardLayout>;

    return (
        <DashboardLayout type="admin">
            <div className="space-y-5" data-testid="admin-page-editor">
                <div className="flex items-center gap-4">
                    <Link to="/admin/pages"><Button variant="ghost" size="sm" className="text-white/40 hover:text-white"><ArrowLeft className="w-4 h-4" /></Button></Link>
                    <div>
                        <h1 className="text-xl font-bold text-white">{page.title}</h1>
                        <p className="text-xs text-white/30">Drag sections to reorder, toggle visibility, edit content</p>
                    </div>
                </div>

                <div className="space-y-3">
                    {page.sections.map((section, index) => (
                        <Card key={section.id} className={`border transition-colors ${section.enabled ? 'bg-[#121620] border-white/10' : 'bg-[#0D1017] border-white/5 opacity-60'}`} data-testid={`section-${section.id}`}>
                            <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpandedId(expandedId === section.id ? null : section.id)}>
                                <GripVertical className="w-4 h-4 text-white/20 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gold/60 font-mono">{index + 1}</span>
                                        <span className="text-sm font-medium text-white truncate">{section.label}</span>
                                        <span className="text-[10px] text-white/20 bg-white/5 px-1.5 py-0.5 rounded">{section.type}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                                    <button onClick={() => moveSection(index, -1)} disabled={index === 0} className="p-1 rounded hover:bg-white/5 text-white/30 hover:text-white disabled:opacity-20" data-testid={`move-up-${section.id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => moveSection(index, 1)} disabled={index === page.sections.length - 1} className="p-1 rounded hover:bg-white/5 text-white/30 hover:text-white disabled:opacity-20" data-testid={`move-down-${section.id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                                    <Switch checked={section.enabled} onCheckedChange={v => toggleSection(section.id, v)} data-testid={`toggle-${section.id}`} />
                                </div>
                                {expandedId === section.id ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                            </div>
                            {expandedId === section.id && (
                                <CardContent className="px-4 pb-4 pt-0 border-t border-white/5">
                                    <div className="pt-4 space-y-4">
                                        {renderContentEditor(section)}
                                        <div className="flex justify-end">
                                            <Button onClick={() => saveSection(section.id)} disabled={saving} size="sm" className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid={`save-${section.id}`}>
                                                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Section
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            )}
                        </Card>
                    ))}
                </div>
            </div>
        </DashboardLayout>
    );
}
